import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './../AuthContext';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { site_url } from "./../config.js";
import axios from 'axios';
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';

function Workers() {
  const { user } = React.useContext(AuthContext);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const observer = useRef();
  const lastWorkerElementRef = useRef();

  const ITEMS_PER_PAGE = 20;

  // Fetch workers from API
  const fetchWorkers = useCallback(async (pageNum, search = '') => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await axios.post(site_url + '/workers_list', {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        search: search
      });

      if (response.data.status === 'success') {
        const newWorkers = response.data.workers || [];
        
        if (pageNum === 1) {
          setWorkers(newWorkers);
        } else {
          setWorkers(prev => [...prev, ...newWorkers]);
        }
        
        setHasMore(newWorkers.length === ITEMS_PER_PAGE);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
      Swal.fire('Error', 'Failed to fetch workers', 'error');
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Search workers
  const searchWorkers = useCallback(async (searchTerm) => {
    if (searchTerm.trim() === '') {
      setFilteredWorkers([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setLoading(true);
    
    try {
      const response = await axios.post(site_url + '/workers_list', {
        page: 1,
        limit: 100, // Get more results for search
        search: searchTerm
      });

      if (response.data.status === 'success') {
        setFilteredWorkers(response.data.workers || []);
      }
    } catch (error) {
      console.error('Error searching workers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setFilteredWorkers([]);
      setIsSearching(false);
    } else {
      // Debounce search
      const timeoutId = setTimeout(() => {
        searchWorkers(value);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  };

  // Load more workers
  const loadMore = useCallback(() => {
    if (!loading && hasMore && !isSearching) {
      fetchWorkers(page + 1, searchTerm);
    }
  }, [loading, hasMore, page, searchTerm, isSearching, fetchWorkers]);

  // Intersection Observer for infinite scroll
  const lastWorkerElementRefCallback = useCallback((node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isSearching) {
        loadMore();
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore, isSearching, loadMore]);

  // Initial load
  useEffect(() => {
    fetchWorkers(1);
  }, []);

  // Get workers to display
  const displayWorkers = isSearching ? filteredWorkers : workers;

  // Handle worker view
  const handleWorkerView = (worker) => {
    Swal.fire({
      title: 'Worker Details',
      html: `
        <div style="text-align: left;">
          <p><strong>Name:</strong> ${worker.w_name || 'N/A'}</p>
          <p><strong>Phone:</strong> ${worker.w_phone || 'N/A'}</p>
          <p><strong>Email:</strong> ${worker.w_email || 'N/A'}</p>
          <p><strong>Address:</strong> ${worker.w_address || 'N/A'}</p>
          <p><strong>Type:</strong> ${worker.w_type == 1 ? 'Admin' : 'Worker'}</p>
          <p><strong>Status:</strong> ${worker.w_status == 1 ? 'Active' : 'Inactive'}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Edit Worker',
      cancelButtonText: 'Close',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then((result) => {
      if (result.isConfirmed) {
        // Navigate to edit worker page
        window.location.href = `/worker/edit/${worker.id}`;
      }
    });
  };

  return (
    <>
      <div id="appCapsule">
        {/* Header */}
        <div className="section mt-2">
          <div className="section-heading">
            <h2>Workers List</h2>
            <div className="float-right">
             
            </div>
          </div>
        </div>

        {/* Search Box */}
        <div className="section">
          <div className="form-group">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Search workers by name, phone, email..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        {/* Workers List */}
        <div className="section">
          {loading && workers.length === 0 && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}

          {displayWorkers.length === 0 && !loading && (
            <div className="text-center py-4">
              <p className="text-muted">No workers found</p>
            </div>
          )}

          <div className="transactions">
            {displayWorkers.map((worker, index) => (
              <div
                key={worker.id || index}
                ref={index === displayWorkers.length - 1 ? lastWorkerElementRefCallback : null}
                className="item"
                onClick={() => handleWorkerView(worker)}
                style={{ cursor: 'pointer' }}
              >
                <div className="detail">
                  <div className="image-block imaged w48">
                    <i className="fas fa-user-tie text-primary" style={{ fontSize: '48px' }}></i>
                  </div>
                  <div>
                    <strong>{worker.w_name || 'N/A'}</strong>
                    <p className="text-muted mb-1">
                      <i className="fas fa-phone"></i> {worker.w_phone || 'N/A'}
                    </p>
                    <p className="text-muted mb-0">
                      <i className="fas fa-envelope"></i> {worker.w_email || 'N/A'}
                    </p>
                    <div className="mt-1">
                      <span className={`badge ${worker.w_type == 1 ? 'badge-success' : 'badge-info'}`}>
                        {worker.w_type == 1 ? 'Admin' : 'Worker'}
                      </span>
                      
                    </div>
                  </div>
                </div>
                <div className="right">
                  <div className="price text-primary">
                    <i className="fas fa-eye"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Loading indicator for pagination */}
          {loading && workers.length > 0 && (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="sr-only">Loading more...</span>
              </div>
              <p className="text-muted mt-2">Loading more workers...</p>
            </div>
          )}

          {/* End of list indicator */}
          {!hasMore && workers.length > 0 && !isSearching && (
            <div className="text-center py-3">
              <p className="text-muted">No more workers to load</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
      <Sidebar />
    </>
  );
}

export default Workers;
